import React, { Component } from 'react';
import RiskFactorEntry from '../util/RiskFactorEntry';
import { fetchRiskConfig, updateRiskConfig } from '../redux/risk/actions';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import {
    getCanUpdateRiskConfig,
    getHoldsRiskConfigForSomeBrandsOnly,
    getSelectedJurisdictionCode,
    isSandboxSelected
} from '../../../../jurisdiction/redux/selectors';
import tooltip from '../../../../shared/tooltip/tooltip.json';
import './RiskAssessmentContent.scss';
import { PageActionsRow } from '../../../../shared/headers/PageActionsRow';
import { BackButton } from '../../../../shared/button/back-button/BackButton';
import { InfoTooltip } from '../../../../shared/info-tooltip/InfoTooltip';
import { SpinLoader } from '../../../../shared/spin-loader/SpinLoader';
import { ReadOnlyNote } from '../../../../shared/read-only-note/ReadOnlyNote';
import { SandboxNote } from '../../../../shared/sandbox/SandboxNote';

export const RiskFactorEnum = Object.freeze({
    COUNTRY_RISK: 'COUNTRY_RISK',
    DELIVERY_CHANNELS: 'DELIVERY_CHANNELS',
    PRODUCT_AND_SERVICES: 'PRODUCT_AND_SERVICES',
    CHASING_LOSSES: 'CHASING_LOSSES',
    NET_EXPENDITURE: 'NET_EXPENDITURE',
    FREQUENCY_OF_PLAY: 'FREQUENCY_OF_PLAY',
    FREQUENCY_OF_CHATTING: 'FREQUENCY_OF_CHATTING',
    TRYING_DIFFERENT_GAMES: 'TRYING_DIFFERENT_GAMES',
    IP_ADDRESS_MULTIPLE_PLAYERS: 'IP_ADDRESS_MULTIPLE_PLAYERS'
});

class RiskAssessmentContent extends Component {
    componentDidMount() {
        this.getRiskConfig();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        if (
            this.props.jurisdictionCode !== prevProps.jurisdictionCode ||
            this.props.ruleCategory !== prevProps.ruleCategory
        ) {
            this.getRiskConfig();
        }
    }

    getRiskConfig() {
        const { getRiskConfig, ruleCategory, jurisdictionCode } = this.props;

        getRiskConfig &&
            ruleCategory &&
            jurisdictionCode &&
            getRiskConfig(ruleCategory, jurisdictionCode);
    }

    updateRiskFactor(riskFactorKey, riskFactorDisplay, weight) {
        // The server refuses it too (403); this keeps a click on a read-only slider from asking
        if (!this.props.canUpdate) {
            return;
        }

        return this.props.updateConfig(
            this.props.ruleCategory,
            this.props.jurisdictionCode,
            [{ riskFactorKey, riskFactorDisplay, riskFactorWeight: weight }]
        );
    }

    getTooltipMessage = riskFactorKey => {
        switch (riskFactorKey) {
            case RiskFactorEnum.COUNTRY_RISK:
                return tooltip.modifyWeights.aml.countryRisk;
            case RiskFactorEnum.DELIVERY_CHANNELS:
                return tooltip.modifyWeights.aml.deliveryChannels;
            case RiskFactorEnum.PRODUCT_AND_SERVICES:
                return tooltip.modifyWeights.aml.productsAndServices;
            case RiskFactorEnum.CHASING_LOSSES:
                return tooltip.modifyWeights.rg.chasingLosses;
            case RiskFactorEnum.FREQUENCY_OF_CHATTING:
                return tooltip.modifyWeights.rg.frequencyOfChatting;
            case RiskFactorEnum.FREQUENCY_OF_PLAY:
                return tooltip.modifyWeights.rg.frequencyOdPlay;
            case RiskFactorEnum.NET_EXPENDITURE:
                return tooltip.modifyWeights.rg.netExpenditure;
            case RiskFactorEnum.TRYING_DIFFERENT_GAMES:
                return tooltip.modifyWeights.rg.tryingDifferentGames;
            case RiskFactorEnum.IP_ADDRESS_MULTIPLE_PLAYERS:
                return tooltip.modifyWeights.fraud.sameIpMultiplePlayers;
            default:
                return null;
        }
    };

    render() {
        const {
            data,
            loading,
            jurisdictionCode,
            canUpdate,
            someBrandsOnly,
            sandboxSelected
        } = this.props;
        return (
            <div className="container">
                {/*todo change the ModifyRulesContainer to be more generic to be for weights as well.*/}
                <PageActionsRow>
                    <BackButton />
                </PageActionsRow>
                {jurisdictionCode ? (
                    <div>
                        <div className="risk-factor-heading">
                            <span className="heading1">Risk Factor</span>
                            <span className="heading2 with-tooltip">
                                Weight{' '}
                                <InfoTooltip
                                    message={
                                        tooltip.modifyWeights.weightAdjustment
                                    }
                                />
                            </span>
                        </div>

                        {sandboxSelected && (
                            <SandboxNote>
                                {`${jurisdictionCode} is a sandbox. Its risk weights are a copy: a change here affects only this sandbox, not the jurisdiction it was copied from.`}
                            </SandboxNote>
                        )}

                        {!canUpdate && (
                            <ReadOnlyNote>
                                {someBrandsOnly
                                    ? `Your teams have the permission to change these weights for only some of the brands of ${jurisdictionCode}. The weights of a jurisdiction apply to all its brands, so it takes the permission for every one of them: ask whoever manages your teams.`
                                    : "You don't have permission to change these weights."}
                            </ReadOnlyNote>
                        )}

                        {loading ? (
                            <SpinLoader />
                        ) : (
                            data.map(riskFactor => (
                                <RiskFactorEntry
                                    key={riskFactor.riskFactorKey}
                                    riskFactor={riskFactor.riskFactorDisplay}
                                    riskFactorWeight={
                                        riskFactor.riskFactorWeight
                                    }
                                    riskFactorAttributes={riskFactor.attributes}
                                    readOnly={!canUpdate}
                                    updateRiskFactor={weight =>
                                        this.updateRiskFactor(
                                            riskFactor.riskFactorKey,
                                            riskFactor.riskFactorDisplay,
                                            weight
                                        )
                                    }
                                    tooltipMessage={this.getTooltipMessage(
                                        riskFactor.riskFactorKey
                                    )}
                                />
                            ))
                        )}
                    </div>
                ) : (
                    <div className={'no-jurisdiction'}>
                        Please select a jurisdiction to adjust weights.
                    </div>
                )}
            </div>
        );
    }
}

const mapStateToProps = state => {
    const { loaded, loading, error, data } = state.risk;
    const jurisdictionCode = getSelectedJurisdictionCode(state);

    return {
        jurisdictionCode,
        canUpdate: getCanUpdateRiskConfig(state),
        someBrandsOnly: getHoldsRiskConfigForSomeBrandsOnly(state),
        sandboxSelected: isSandboxSelected(state),
        data,
        loaded,
        loading,
        error
    };
};
const mapDispatchToProps = dispatch => ({
    getRiskConfig: (riskCategory, jurisdictionCode) =>
        dispatch(fetchRiskConfig({ riskCategory, jurisdictionCode })),
    updateConfig: (riskCategory, jurisdictionCode, config) =>
        dispatch(updateRiskConfig({ riskCategory, jurisdictionCode, config }))
});

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(RiskAssessmentContent)
);
